<template>
  <!--
 ParameterCellElementLite, multi-root, нативная разметка вместо q-*.

    Выравнивание ВЕРТИКАЛЬНЫХ ПОЗИЦИЙ подстрок между Parameter-колонками
    одной строки делает useTableModel.normalizeParameterCellsAcrossColumns
    (синхронно при сборке rows). Все Parameter-cells в одной строке имеют
    одинаковое число подстрок в одном порядке ParentRecordId, placeholder'ы
    помечены `__placeholder=true` и рендерят &nbsp;.

    Выравнивание ВЫСОТ подстрок (если контент в разных колонках разной высоты)
    делает useRowParameterHeights в QTLiteRow: ResizeObserver измеряет sub-rows
    и пишет CSS-переменные --qtl-psub-{idx}-h на <tr>. Здесь мы лишь применяем
    `min-height: var(--qtl-psub-{idx}-h, 0)` через style на каждой подстроке.

    Для совместимости с внешними вызовами оставлены setCompareRecord/updateHeights/
 launchRecalcHeights (последние две функции, заглушки, выравнивание делает observer).
  -->
  <div
    v-for="(row, rIdx) in renderRows"
    :key="rIdx"
    class="qtl-param-row"
    :class="{
      'qtl-param-row--match': isMatch(row.elem),
      'qtl-param-row--empty': !!row.elem.__placeholder,
    }"
    :data-qtl-param-idx="(noRowHeightAlign || row.span > 1) ? undefined : row.slotStart"
    :style="rowMinHeightStyle(row)"
    @mouseover="!row.elem.__placeholder && onMouseover(row.elem.ParentRecordId)"
    @mouseleave="!row.elem.__placeholder && onMouseleave(row.elem.ParentRecordId)"
  >
    <span class="qtl-param-row__index">
      {{ rowIndexPrefix(row) }}
    </span>
    <SimpleCellElementLite
      v-if="!row.elem.__placeholder"
      :element="row.elem"
    />
    <!-- Placeholder: пустое содержимое, но min-height из CSS-переменной поднимет высоту
         до уровня соответствующей подстроки соседней колонки. -->
    <span v-else class="qtl-param-row__placeholder">&nbsp;</span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type {
  CellDataParameter,
  TableDataCell,
} from "@/models/interfaces/entity-settings/querytable/querytable";
import SimpleCellElementLite from "./SimpleCellElementLite.vue";

// сентинел для подсчёта групп: заведомо не равен ни одному ParentRecordId
const NO_GROUP = Symbol("no-group");

const props = defineProps<{
  element: TableDataCell;
  tableRowNumber?: number;
  columnIndex?: string;
  /**
   * Подсветка совпадающих подстрок (cross-cell highlight).
   * Идёт от QTLiteRow: при hover на любой подстроке в любой Parameter-ячейке строки
 * сюда передаётся ParentRecordId, все подстроки во ВСЕХ Parameter-ячейках строки
   * с этим ParentRecordId подсвечиваются (поведение оригинального QueryTable.onRecordHover).
   *
   * Тип `string | number` (а не `number`) выбран потому, что ParentRecordId в моделях
   * объявлен как `string` (см. CellDataParameter), но в данных встречаются и
   * числовые id. Сравниваем без принуждения типа, иначе подсветка не сработает.
   */
  hoveredParamRecId?: string | number;
  /**
   * Отключает участие подстрок этой ячейки в построчном выравнивании высот
   * (useRowParameterHeights сканирует весь <tr> по [data-qtl-param-idx] и тянет
   * высоты к общему максимуму по индексу). Для параметров ВНУТРИ рекурсивной
   * складской ячейки (DisplayType 16) это вредно: они вертикально-независимы и не
   * должны влиять на выравнивание верхнеуровневых горизонтальных параметрических
   * колонок. Поэтому в рекурсивном контексте выставляем true: не проставляем
   * data-qtl-param-idx и не навешиваем min-height из CSS-переменной.
   */
  noRowHeightAlign?: boolean;
}>();

const emit = defineEmits<{
  // recId типизируем как `string | number`, потому что ParentRecordId по моделям
  // string, но в реальных данных встречается и number. Ниже компонент сравнивает
  // через String(), без этого подсветка не срабатывает (см. isMatch).
  (e: "changeHoverRecord", payload: { recId: string | number | undefined; rowNum: number }): void;
  (e: "changeHeights", payload: any): void;
}>();

/** Параметры (sub-rows) для рендера. Один-в-один с ParameterCellElement.params,
 *  но с пробросом __placeholder из useTableModel-нормализации. */
const params = computed<any[]>(() =>
  (props.element.Data || []).map((el) => ({
    ...props.element,
    ParentRecordId: (el as CellDataParameter).ParentRecordId,
    Data: [el],
    __placeholder: !!(el as any).__placeholder,
  })),
);

/** Map<ParentRecordId, index> для отображения "1.", "2." перед группами параметров.
 *  Placeholder'ы участвуют наравне (у них может быть тот же ParentRecordId,
 * но рендерим без префикса, он всё равно повторяется). */
const indicesArray = computed<Map<unknown, number>>(() => {
  const map = new Map<unknown, number>();
  let pIndex = 0;
  let oldP: unknown = undefined;
  params.value.forEach((el) => {
    if (el.ParentRecordId !== oldP) {
      pIndex++;
      oldP = el.ParentRecordId;
    }
    map.set(el.ParentRecordId, pIndex);
  });
  return map;
});

// Сколько различных групп (ParentRecordId) в ячейке. Нумерация "1.", "2." нужна
// только когда групп больше одной, чтобы связать подстроки между колонками. При
// единственной группе (одно значение или несколько значений одного ParentRecordId)
// префикс "1." — просто шум, поэтому его прячем.
const groupCount = computed<number>(() => {
  let count = 0;
  // сентинел заведомо не равен ни одному реальному ParentRecordId (включая undefined),
  // чтобы первая группа всегда засчитывалась
  let oldP: unknown = NO_GROUP;
  for (const el of params.value) {
    if (el.ParentRecordId !== oldP) {
      count++;
      oldP = el.ParentRecordId;
    }
  }
  return count;
});

/**
 * Строки для рендера. Обычно один слот = одна подстрока. Но если в подгруппе
 * (последовательные слоты с одним ParentRecordId) у ЭТОЙ колонки ровно одно
 * значение, а сама группа занимает несколько слотов (в соседних колонках у той же
 * записи несколько значений) — схлопываем группу в ОДНУ подстроку, растянутую на
 * высоту всех слотов группы. Так одиночное «объединяющее» значение (например,
 * сумма) визуально накрывает весь блок своей подгруппы, а не висит в первом слоте.
 *
 * span > 1 → подстрока покрывает несколько слотов; её min-height считаем как сумму
 * высот этих слотов + внутренние отступы. Такую подстроку НЕ помечаем
 * data-qtl-param-idx: высоты слотов берутся из «многострочных» колонок (например,
 * названий), а растянутое значение лишь тянется к их сумме.
 */
interface ParamRenderRow {
  elem: any;
  slotStart: number;
  span: number;
  spanIndices: number[];
  isGroupStart: boolean;
}

const renderRows = computed<ParamRenderRow[]>(() => {
  const p = params.value;
  const rows: ParamRenderRow[] = [];
  const disableSpan = !!props.noRowHeightAlign;
  let i = 0;
  while (i < p.length) {
    const gid = p[i].ParentRecordId;
    const canGroup = gid !== undefined && gid !== null && gid !== "";
    // границы подгруппы: подряд идущие слоты с одним ParentRecordId
    let j = i;
    if (canGroup) {
      while (j + 1 < p.length && p[j + 1].ParentRecordId === gid) j++;
    }
    const slotIndices: number[] = [];
    for (let k = i; k <= j; k++) slotIndices.push(k);
    const reals = slotIndices.filter((k) => !p[k].__placeholder);
    if (!disableSpan && canGroup && slotIndices.length > 1 && reals.length === 1) {
      rows.push({
        elem: p[reals[0]],
        slotStart: i,
        span: slotIndices.length,
        spanIndices: slotIndices,
        isGroupStart: true,
      });
    } else {
      slotIndices.forEach((k, idx) => {
        rows.push({
          elem: p[k],
          slotStart: k,
          span: 1,
          spanIndices: [k],
          isGroupStart: idx === 0,
        });
      });
    }
    i = j + 1;
  }
  return rows;
});

function rowIndexPrefix(row: ParamRenderRow): string {
  if (row.elem.__placeholder) return "";
  if (groupCount.value <= 1) return "";
  if (!row.isGroupStart) return "";
  const idx = indicesArray.value.get(row.elem.ParentRecordId);
  if (!idx || !row.elem.ParentRecordId) return "";
  return idx + ". ";
}

// min-height подстроки. Обычная подстрока тянется к высоте своего слота, растянутая
// (span > 1) — к сумме высот покрытых слотов плюс внутренние 3px-отступы между ними
// (см. margin-bottom .qtl-param-row), чтобы точно совпасть с высотой блока подгруппы
// в соседних колонках.
function rowMinHeightStyle(row: ParamRenderRow): Record<string, string> | undefined {
  if (props.noRowHeightAlign) return undefined;
  if (row.span <= 1) {
    return { minHeight: `var(--qtl-psub-${row.slotStart}-h, 0)` };
  }
  const parts = row.spanIndices.map((idx) => `var(--qtl-psub-${idx}-h, 0)`);
  const gapPx = (row.span - 1) * 3;
  return { minHeight: `calc(${parts.join(" + ")} + ${gapPx}px)` };
}

// Локальный hover-state. Тип сознательно широкий (string | number | undefined),
// см. комментарий к prop hoveredParamRecId выше, ParentRecordId может быть как
// строкой, так и числом в зависимости от источника данных.
const hoveredParentRecId = ref<string | number | undefined>(undefined);

watch(hoveredParentRecId, (v) => {
  emit("changeHoverRecord", { recId: v, rowNum: props.tableRowNumber ?? 0 });
});

// Когда сверху прилетает новое значение hoveredParamRecId, логируем,
// сколько подстрок в данной ячейке имеют совпадающий ParentRecordId.
// Если 0 при ненулевом target, подсветки не будет, виден реальный
// массив доступных ParentRecordId этой ячейки → видно, расходятся ли
// типы (string/number) и есть ли вообще ParentRecordId в данных.
watch(() => props.hoveredParamRecId, (target) => {
  if (!(typeof window !== "undefined") || !(window as any).__qtlDebugHighlight) return;
  if (target === undefined || target === null || target === "" || target === 0) return;
  const recIds = params.value.map((p) => p.ParentRecordId);
  const matches = recIds.filter((r) => r !== undefined && r !== null && String(r) === String(target));
  // eslint-disable-next-line no-console
  console.debug("[QTL highlight] param.target-changed", {
    column: props.columnIndex,
    target,
    typeofTarget: typeof target,
    paramsCount: params.value.length,
    paramsRecIds: recIds,
    matches: matches.length,
  });
});

function onMouseover(arg: string | number | undefined) {
  hoveredParentRecId.value = arg;
  if ((typeof window !== "undefined") && (window as any).__qtlDebugHighlight) {
    // eslint-disable-next-line no-console
    console.debug("[QTL highlight] param.onMouseover", {
      column: props.columnIndex,
      recId: arg,
      typeofRecId: typeof arg,
      paramsCount: params.value.length,
      paramsRecIds: params.value.map((p) => p.ParentRecordId),
    });
  }
}

function onMouseleave(arg: string | number | undefined) {
  // Сравнение строгое, но мы НЕ конвертируем типы, потому что arg всегда
  // совпадает по типу с тем, что положили в onMouseover (одно и то же поле
  // из одного источника). Сброс делаем в undefined → корректно гасит подсветку.
  if (hoveredParentRecId.value === arg) hoveredParentRecId.value = undefined;
}

/**
 * Подсветка строки (match-rec из оригинала). Источник истины, prop hoveredParamRecId,
 * который выставляется на уровне QTLiteRow при hover на ЛЮБОЙ Parameter-ячейке
 * этой строки. Это даёт cross-cell highlight как в QueryTable.onRecordHover.
 *
 * Сравнение через String(), потому что ParentRecordId объявлен как string в моделях,
 * но в реальных данных может приходить и как number. Без принуждения было бы
 * `'5' === 5` → false, и подсветка просто не появлялась.
 */
function isMatch(elem: any): boolean {
  if (elem.__placeholder) return false;
  const target = props.hoveredParamRecId;
  if (target === undefined || target === null || target === "" || target === 0) return false;
  if (elem.ParentRecordId === undefined || elem.ParentRecordId === null) return false;
  const eq = String(elem.ParentRecordId) === String(target);
  if (eq && (typeof window !== "undefined") && (window as any).__qtlDebugHighlight) {
    // eslint-disable-next-line no-console
    console.debug("[QTL highlight] param.isMatch HIT", {
      column: props.columnIndex,
      target,
      elemRecId: elem.ParentRecordId,
    });
  }
  return eq;
}

/**
 * QueryTable вызывал setCompareRecord через ref-карту parameter_cells.
 * Здесь оставлены заглушки, чтобы внешние вызовы не падали.
 * updateHeights/launchRecalcHeights делает useRowParameterHeights + CSS-переменные.
 */
function setCompareRecord(_val: number) {}
function updateHeights(_h: any) { /* handled by row-level CSS vars */ }
function launchRecalcHeights() { /* handled by row-level CSS vars */ }

defineExpose({ setCompareRecord, updateHeights, launchRecalcHeights });
</script>

<style lang="scss" scoped>
.qtl-param-row {
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  max-width: 100%;
  min-width: 0;
  /* Аналог .smooth-height из ParameterCellElement. */
  transition: background-color 0.06s ease;
  border-radius: 2px;
  /* min-height приходит инлайном через style="min-height: var(--qtl-psub-N-h, 0)".
 Placeholder без переменной (=0) не занимает места, что верно: если в данной
     колонке для индекса N нет подстроки, а в соседней есть, переменная придёт от
     useRowParameterHeights и плейсхолдер заматчит высоту. */
}

/* Микро-отступ между подстроками, чтобы визуально отделить значения одно от
   другого. Margin не входит в getBoundingClientRect().height, поэтому на
   построчное выравнивание высот (useRowParameterHeights) не влияет: во всех
   колонках подстрок поровну и отступ одинаковый, границы строк совпадают. */
.qtl-param-row:not(:last-child) {
  margin-bottom: 3px;
}

.qtl-param-row--match {
  background-color: #D3E3FD;
  border-color: #D3E3FD;
}

.qtl-param-row--empty {
  /* Не должно перехватывать hover/click, это структурный плейсхолдер. */
  pointer-events: none;
}

.qtl-param-row__index {
  margin-top: 0;
  margin-right: 2px;
  padding: 0;
  min-width: 0;
  flex: 0 0 auto;
  font-size: 13px;
}

/* Контентная часть Parameter подстроки должна уметь сжиматься в ширину
   колонки (иначе flex-item с auto min-width визуально "выползает"). */
.qtl-param-row > :not(.qtl-param-row__index) {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
}

.qtl-param-row__placeholder {
 /* Невидимый, но занимает baseline, нужно для корректного flex-layout
     (без него flex-row может схлопнуться к нулю). */
  display: inline-block;
  width: 1px;
}

/* Для параметров разрешаем перенос в любой точке: длинные токены без пробелов
   (артикулы, коды, payload) не должны блокировать сжатие колонки. */
.qtl-param-row :deep(.qtl-simple-html) {
  overflow-wrap: anywhere;
  word-break: normal;
}
</style>
